/**
 * TrainingMobileTabBar — the Training section's bottom tab bar on mobile.
 *
 * Mirrors MobileTabNavigation's chrome (fixed bottom, brand-green bar, flex tabs,
 * the same `--mobile-nav-height` measurement) and the design's own mobile nav:
 * four primary tabs (Learn / Cards / Practice / Exam) plus a "More" sheet holding
 * the remaining screens (Dashboard / CDL Roadmap / Missed Questions).
 */
import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu } from 'lucide-react';
import { cn } from '@/lib/utils';
import { TRAINING_NAV_ITEMS, type TrainingKey } from '@/pages/training/trainingNav';

const TAB_KEYS: TrainingKey[] = ['learn', 'cards', 'practice', 'exam'];
const MORE_KEYS: TrainingKey[] = ['home', 'roadmap', 'review'];
// short tab labels, verbatim from the design's TAB_LABEL
const TAB_LABEL: Partial<Record<TrainingKey, string>> = {
  learn: 'Learn',
  cards: 'Cards',
  practice: 'Practice',
  exam: 'Exam',
};

const byKey = (key: TrainingKey) => TRAINING_NAV_ITEMS.find((i) => i.key === key)!;

export function TrainingMobileTabBar() {
  const location = useLocation();
  const navRef = useRef<HTMLElement>(null);
  const [moreOpen, setMoreOpen] = useState(false);

  useEffect(() => {
    const updateNavHeight = () => {
      if (navRef.current) {
        document.documentElement.style.setProperty(
          '--mobile-nav-height',
          `${navRef.current.offsetHeight}px`,
        );
      }
    };
    updateNavHeight();
    window.addEventListener('resize', updateNavHeight);
    return () => {
      window.removeEventListener('resize', updateNavHeight);
      document.documentElement.style.removeProperty('--mobile-nav-height');
    };
  }, []);

  const isActive = (key: TrainingKey) => location.pathname === byKey(key).path;
  const moreActive = MORE_KEYS.some(isActive);

  const tabClass = (active: boolean) =>
    cn(
      'flex flex-col items-center justify-center flex-1 h-full transition-all',
      active ? 'text-gray-900 bg-black/10' : 'text-gray-800 hover:bg-black/5',
    );

  return (
    <>
      {moreOpen && (
        <>
          <div
            className="fixed inset-0 z-20 bg-black/40"
            onClick={() => setMoreOpen(false)}
            aria-hidden="true"
          />
          <div className="fixed bottom-16 left-0 right-0 z-30 border-t border-border bg-card p-2">
            {MORE_KEYS.map((key) => {
              const item = byKey(key);
              const Icon = item.icon;
              const active = isActive(key);
              return (
                <Link
                  key={key}
                  to={item.path}
                  onClick={() => setMoreOpen(false)}
                  className={cn(
                    'flex items-center gap-3 rounded-lg px-4 py-3 text-sm font-medium transition-colors',
                    active
                      ? 'bg-[#8FB560]/10 text-[#8FB560]'
                      : 'text-foreground hover:bg-accent',
                  )}
                >
                  <Icon className="h-5 w-5" />
                  {item.name}
                </Link>
              );
            })}
          </div>
        </>
      )}

      <nav
        ref={navRef}
        data-mobile-nav
        className="fixed bottom-0 left-0 right-0 z-30 bg-[#8FB560] border-t border-[#7A9A52]"
      >
        <div className="flex items-center justify-around h-16">
          {TAB_KEYS.map((key) => {
            const item = byKey(key);
            const Icon = item.icon;
            return (
              <Link
                key={key}
                to={item.path}
                onClick={() => setMoreOpen(false)}
                className={tabClass(isActive(key))}
              >
                <Icon className="h-5 w-5 mb-1" />
                <span className="text-xs font-medium">{TAB_LABEL[key]}</span>
              </Link>
            );
          })}
          <button
            type="button"
            onClick={() => setMoreOpen((o) => !o)}
            aria-label="More training screens"
            aria-expanded={moreOpen}
            className={tabClass(moreActive || moreOpen)}
          >
            <Menu className="h-5 w-5 mb-1" />
            <span className="text-xs font-medium">More</span>
          </button>
        </div>
      </nav>
    </>
  );
}
